import { LoadingSpinner } from '@kira/checkpoint-shared-frontend';
import React from 'react';
import { twMerge } from 'tailwind-merge';

export enum ButtonVariant {
  CANCEL = 'cancel',
  DELETE = 'delete',
  PRIMARY = 'primary',
  SAVE = 'save',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  TRANSPARENT = 'transparent',
  SOLID = 'solid',
}
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonText: string | JSX.Element;
  buttonClassNames?: string;
  variant: ButtonVariant;
  isLoading?: boolean;
  dataTestId?: string;
}

const Button = ({
  buttonText,
  buttonClassNames,
  variant,
  isLoading = false,
  dataTestId,
  ...rest
}: ButtonProps) => {
  const cancelClassName = twMerge(
    'bg-transparent',
    'enabled:hover:bg-kira-slate-100',
    'text-kira-slate-800'
  );
  const deleteClassName = twMerge(
    'bg-kira-red-800',
    'enabled:hover:bg-kira-red-900',
    'text-kira-slate-0'
  );
  const primaryClassName = twMerge(
    'bg-kira-blue-800',
    'enabled:hover:bg-kira-blue-900',
    'text-kira-slate-0'
  );
  const saveClassName = twMerge(
    'bg-kira-green-800',
    'enabled:hover:bg-kira-green-900',
    'text-kira-slate-0'
  );
  const secondaryClassName = twMerge(
    'bg-kira-blue-100',
    'enabled:hover:bg-kira-blue-800',
    'text-kira-slate-800',
    'enabled:hover:text-kira-slate-0'
  );
  const tertiaryClassName = twMerge(
    'bg-kira-slate-100',
    'enabled:hover:bg-kira-slate-200',
    'text-kira-slate-800'
  );
  const transparentClassName = twMerge(
    'bg-transparent',
    'enabled:hover:bg-kira-blue-100',
    'text-kira-slate-800'
  );
  const solidClassname = twMerge(
    'bg-kira-slate-0',
    'enabled:hover:bg-kira-blue-100',
    'text-kira-blue-800'
  );
  const buttonClassName = twMerge(
    'disabled:opacity-30',
    'duration-200',
    'ease-in-out',
    'enabled:cursor-pointer',
    'prose-button-sm',
    'px-4',
    'py-1.5',
    'rounded',
    'self-center',
    'transition',
    'justify-center',
    'flex',
    `${variant === ButtonVariant.CANCEL ? cancelClassName : ''}`,
    `${variant === ButtonVariant.DELETE ? deleteClassName : ''}`,
    `${variant === ButtonVariant.PRIMARY ? primaryClassName : ''}`,
    `${variant === ButtonVariant.SAVE ? saveClassName : ''}`,
    `${variant === ButtonVariant.SECONDARY ? secondaryClassName : ''}`,
    `${variant === ButtonVariant.TERTIARY ? tertiaryClassName : ''}`,
    `${variant === ButtonVariant.TRANSPARENT ? transparentClassName : ''}`,
    `${variant === ButtonVariant.SOLID ? solidClassname : ''}`,
    `${isLoading && 'text-transparent disabled:opacity-100'}`,
    buttonClassNames || ''
  );

  const spinnerClassNames = twMerge(
    'absolute',
    `${
      variant === ButtonVariant.SECONDARY
        ? 'text-kira-slate-800'
        : 'text-kira-slate-0'
    }`
  );

  return (
    <button
      type="button"
      className={buttonClassName}
      data-testid={dataTestId}
      {...rest}
    >
      <p className="static">{buttonText}</p>
      {isLoading && <LoadingSpinner spinnerClassNames={spinnerClassNames} />}
    </button>
  );
};

export default Button;
